import {
    AmbientLight,
    Box3,
    BoxGeometry,
    CanvasTexture,
    Color,
    DirectionalLight,
    DoubleSide,
    Group,
    LinearFilter,
    LinearMipmapLinearFilter,
    Mesh,
    MeshBasicMaterial,
    MeshStandardMaterial,
    Object3D,
    Object3DEventMap,
    OrthographicCamera,
    PlaneGeometry,
    Quaternion,
    Raycaster,
    Scene,
    Shape,
    ShapeGeometry,
    SRGBColorSpace,
    Vector2,
    Vector3,
    Vector4
} from 'three';
// BoxGeometry still needed for pick targets
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
// @ts-ignore
import { TrackballControls } from '../trackballcontrols';
import * as TWEEN from '@tweenjs/tween.js';
import { Engine } from '../engine';

class ViewCubeHelper extends Object3D {

    isViewHelper: boolean;
    center: Vector3;
    animating: boolean;
    render: (renderer: any) => void;
    handlePointerDown: (event: PointerEvent) => boolean;
    handlePointerMove: (event: PointerEvent) => boolean;
    handlePointerUp: (event: PointerEvent) => boolean;
    handlePointerLeave: () => void;
    setLabels: (labelX: string, labelY: string, labelZ: string) => void;
    setLabelStyle: (font: string, color: string) => void;
    setTheme: (isDark: boolean) => void;
    update: (delta: number) => void;
    dispose: () => void;

    centerCameraOnObjectOrthographic: (object: Object3D<Object3DEventMap>) => Promise<any>;
    fitCameraToGroupAndSetIsometricOrthographic: (group: Object3D<Object3DEventMap>, zoom?: number) => void;
    rotateCameraToSide: (axis: 'posX' | 'negX' | 'posY' | 'negY' | 'posZ' | 'negZ') => void;
    rotateCameraToNormal: (normal: Vector3, targetPoint?: Vector3) => void;
    centerCameraOnPoint: (point: Vector3) => void;

    fitCameraToObject: (group: Object3D<Object3DEventMap> | Box3, zoom?: number) => void;

    constructor(
        engine: Engine,
        camera: OrthographicCamera,
        controls: TrackballControls,
        group: Group,
        domElement: HTMLElement,
        viewHelperContainer: HTMLDivElement
    ) {

        super();

        this.isViewHelper = true;
        this.animating = false;
        this.center = new Vector3();

        const worldUp = new Vector3(0, 0, 1);
        const altUp = new Vector3(0, 1, 0);

        const options: any = {
            font: '700 28px "SF Pro Display", sans-serif',
            color: '#f5f7fb'
        };
        const containerPadding = 4;

        // Create a dedicated scene for proper lighting support with MeshStandardMaterial
        const helperScene = new Scene();

        const cubeGroup = new Group();
        cubeGroup.scale.setScalar(1.25); // Smaller cube to fit container
        const overlayGroup = new Group();
        this.add(overlayGroup);
        this.add(cubeGroup);
        // Add this helper to the scene for rendering
        helperScene.add(this);

        const raycaster = new Raycaster();
        const mouse = new Vector2();

        const orthoCamera = new OrthographicCamera(-1.5, 1.5, 1.5, -1.5, 0, 6);
        orthoCamera.position.set(0, 0, 3);

        // Track current theme state
        let currentThemeIsDark = document.documentElement.getAttribute('data-theme') === 'dark';

        // Brand highlight color - signature green
        const highlightColor = new Color('#a0ff00');

        // Theme-aware cube colors
        const cubeDarkColor = new Color('#1e1e1e');
        const cubeLightColor = new Color('#ffffff');

        // Bold techy cube - dark charcoal with rounded corners
        const cubeSize = 1;
        const cornerRadius = 0.15; // Radius of rounded corners (more visible)
        const cornerSegments = 5; // Smoothness of corners
        const cubeGeometry = new RoundedBoxGeometry(cubeSize, cubeSize, cubeSize, cornerSegments, cornerRadius);

        // Add lighting for proper shading of rounded corners
        // Lights are added to overlayGroup (not cubeGroup) so they stay fixed while cube rotates
        const ambientLight = new AmbientLight(0xffffff, 0.35);
        overlayGroup.add(ambientLight);

        // Strong key light for specular highlights on curved surfaces
        const keyLight = new DirectionalLight(0xffffff, 1.8);
        keyLight.position.set(1, 1, 3);
        overlayGroup.add(keyLight);

        // Secondary light from opposite side
        const fillLight = new DirectionalLight(0xffffff, 0.8);
        fillLight.position.set(-2, 0, 1);
        overlayGroup.add(fillLight);

        // Subtle green accent light from behind (reduced intensity)
        const rimLight = new DirectionalLight(0xa0ff00, 0.3);
        rimLight.position.set(0, 0, -3);
        overlayGroup.add(rimLight);

        // Face color - theme-aware with subtle reflectivity
        const faceBaseColor = currentThemeIsDark ? cubeDarkColor.clone() : cubeLightColor.clone();
        const cubeMaterial = new MeshStandardMaterial({
            color: faceBaseColor,
            roughness: currentThemeIsDark ? 0.35 : 0.05,
            metalness: currentThemeIsDark ? 0.15 : 0.0,
            toneMapped: false
        });

        const cube = new Mesh(cubeGeometry, cubeMaterial);
        cube.renderOrder = 1;
        cubeGroup.add(cube);

        // Highlight dimensions
        const half = cubeSize / 2;
        const edgeStripWidth = 0.12; // Width of edge highlight strip on each face
        const cornerStripSize = 0.12; // Size of corner highlight on each face
        const surfaceOffset = 0.003; // Slight offset to prevent z-fighting

        // Edge/corner highlight material - semi-transparent green
        const edgeHighlightMaterial = new MeshBasicMaterial({
            color: highlightColor,
            transparent: true,
            opacity: 0,
            toneMapped: false,
            depthTest: false,
            side: DoubleSide
        });

        // Outline material for edge/corner highlights - bright green for visibility on dark faces
        const outlineColor = new Color('#a0ff00'); // Brand green for outlines
        const stripOutlineMaterial = new LineMaterial({
            color: outlineColor,
            transparent: true,
            opacity: 0,
            linewidth: 1.5,
            depthTest: false,
            resolution: new Vector2(window.innerWidth, window.innerHeight)
        });

        // Helper to create a rounded rectangle shape
        const createRoundedRectShape = (width: number, height: number, radius: number): Shape => {
            const shape = new Shape();
            const hw = width / 2;
            const hh = height / 2;
            const r = Math.min(radius, hw, hh); // Clamp radius to half of smallest dimension

            shape.moveTo(-hw + r, -hh);
            shape.lineTo(hw - r, -hh);
            shape.quadraticCurveTo(hw, -hh, hw, -hh + r);
            shape.lineTo(hw, hh - r);
            shape.quadraticCurveTo(hw, hh, hw - r, hh);
            shape.lineTo(-hw + r, hh);
            shape.quadraticCurveTo(-hw, hh, -hw, hh - r);
            shape.lineTo(-hw, -hh + r);
            shape.quadraticCurveTo(-hw, -hh, -hw + r, -hh);

            return shape;
        };

        // Helper to create rounded rectangle outline positions (for LineSegments2)
        const createRoundedRectOutlinePositions = (width: number, height: number, radius: number, segments: number = 4): number[] => {
            const positions: number[] = [];
            const hw = width / 2;
            const hh = height / 2;
            const r = Math.min(radius, hw, hh);

            // Helper to add a line segment
            const addLine = (x1: number, y1: number, x2: number, y2: number) => {
                positions.push(x1, y1, 0, x2, y2, 0);
            };

            // Helper to add corner arc segments
            const addCornerArc = (cx: number, cy: number, startAngle: number) => {
                for (let i = 0; i < segments; i++) {
                    const a1 = startAngle + (i / segments) * (Math.PI / 2);
                    const a2 = startAngle + ((i + 1) / segments) * (Math.PI / 2);
                    addLine(
                        cx + Math.cos(a1) * r, cy + Math.sin(a1) * r,
                        cx + Math.cos(a2) * r, cy + Math.sin(a2) * r
                    );
                }
            };

            // Bottom edge
            addLine(-hw + r, -hh, hw - r, -hh);
            // Bottom-right corner
            addCornerArc(hw - r, -hh + r, -Math.PI / 2);
            // Right edge
            addLine(hw, -hh + r, hw, hh - r);
            // Top-right corner
            addCornerArc(hw - r, hh - r, 0);
            // Top edge
            addLine(hw - r, hh, -hw + r, hh);
            // Top-left corner
            addCornerArc(-hw + r, hh - r, Math.PI / 2);
            // Left edge
            addLine(-hw, hh - r, -hw, -hh + r);
            // Bottom-left corner
            addCornerArc(-hw + r, -hh + r, Math.PI);

            return positions;
        };

        // Radius for rounded highlights
        const highlightCornerRadius = 0.04;

        // Helper to create a rounded strip on a face (fill only, no outline)
        const createFaceStrip = (width: number, height: number, position: Vector3, rotationAxis: 'x' | 'y' | 'z', rotationAngle: number) => {
            const shape = createRoundedRectShape(width, height, highlightCornerRadius);
            const geo = new ShapeGeometry(shape);
            const mesh = new Mesh(geo, edgeHighlightMaterial.clone());
            mesh.position.copy(position);
            if (rotationAxis === 'x') mesh.rotation.x = rotationAngle;
            else if (rotationAxis === 'y') mesh.rotation.y = rotationAngle;
            else mesh.rotation.z = rotationAngle;
            mesh.visible = false;
            mesh.renderOrder = 3;
            return mesh;
        };

        // Helper to create a rounded strip with outline
        const createStripWithOutline = (width: number, height: number, position: Vector3, rotationAxis: 'x' | 'y' | 'z', rotationAngle: number) => {
            // Create fill mesh with rounded corners
            const mesh = createFaceStrip(width, height, position, rotationAxis, rotationAngle);

            // Create rounded outline
            const outlinePositions = createRoundedRectOutlinePositions(width, height, highlightCornerRadius);
            const outlineGeo = new LineSegmentsGeometry();
            outlineGeo.setPositions(outlinePositions);
            const outline = new LineSegments2(outlineGeo, stripOutlineMaterial.clone());
            outline.position.copy(position);
            if (rotationAxis === 'x') outline.rotation.x = rotationAngle;
            else if (rotationAxis === 'y') outline.rotation.y = rotationAngle;
            else outline.rotation.z = rotationAngle;
            outline.visible = false;
            outline.renderOrder = 4;

            return { mesh, outline };
        };

        // Face highlights - central rectangle with margin from edges
        // The rectangle doesn't touch the edges, leaving room for edge/corner zones
        const faceHighlights: { mesh: Mesh; outline: LineSegments2; direction: Vector3 }[] = [];
        const faceHighlightMargin = edgeStripWidth; // Margin from face edges
        const faceHighlightSize = cubeSize - faceHighlightMargin * 2; // Size of central rectangle

        // Outline material for face highlights - use same dark color
        const faceOutlineMaterial = new LineMaterial({
            color: outlineColor,
            transparent: true,
            opacity: 0,
            linewidth: 2,
            depthTest: false,
            resolution: new Vector2(window.innerWidth, window.innerHeight)
        });

        // Face highlight corner radius (larger for the bigger face rectangles)
        const faceHighlightRadius = 0.06;

        // Create face highlight (rounded rectangle + outline) for each of the 6 faces
        const createFaceHighlight = (faceDir: Vector3) => {
            const isX = Math.abs(faceDir.x) > 0.5;
            const isY = Math.abs(faceDir.y) > 0.5;

            // Create the fill mesh with rounded corners
            const shape = createRoundedRectShape(faceHighlightSize, faceHighlightSize, faceHighlightRadius);
            const geo = new ShapeGeometry(shape);
            const mesh = new Mesh(geo, edgeHighlightMaterial.clone());

            // Position and rotate based on face direction
            if (isX) {
                mesh.position.set(faceDir.x * (half + surfaceOffset), 0, 0);
                mesh.rotation.y = faceDir.x > 0 ? Math.PI / 2 : -Math.PI / 2;
            } else if (isY) {
                mesh.position.set(0, faceDir.y * (half + surfaceOffset), 0);
                mesh.rotation.x = faceDir.y > 0 ? -Math.PI / 2 : Math.PI / 2;
            } else {
                mesh.position.set(0, 0, faceDir.z * (half + surfaceOffset));
                mesh.rotation.x = faceDir.z > 0 ? 0 : Math.PI;
            }

            mesh.visible = false;
            mesh.renderOrder = 3;
            cubeGroup.add(mesh);

            // Create rounded outline for the rectangle
            const outlinePositions = createRoundedRectOutlinePositions(faceHighlightSize, faceHighlightSize, faceHighlightRadius);
            const outlineGeo = new LineSegmentsGeometry();
            outlineGeo.setPositions(outlinePositions);
            const outline = new LineSegments2(outlineGeo, faceOutlineMaterial.clone());
            outline.position.copy(mesh.position);
            outline.rotation.copy(mesh.rotation);
            outline.visible = false;
            outline.renderOrder = 4;
            cubeGroup.add(outline);

            return { mesh, outline };
        };

        // Create face highlights for all 6 faces
        [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]].forEach(([x, y, z]) => {
            const dir = new Vector3(x, y, z);
            const { mesh, outline } = createFaceHighlight(dir);
            faceHighlights.push({ mesh, outline, direction: dir });
        });

        // Edge highlights - each edge has TWO strips with outlines (one on each adjacent face)
        const edgeHighlights: { meshes: Mesh[]; outlines: LineSegments2[]; direction: Vector3 }[] = [];

        // 4 edges parallel to X axis (where Y and Z faces meet)
        [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sy, sz]) => {
            const meshes: Mesh[] = [];
            const outlines: LineSegments2[] = [];
            const edgeLen = cubeSize - cornerStripSize * 2; // Leave room for corners

            // Strip on Y face - position at the edge where Z meets Y
            const yResult = createStripWithOutline(
                edgeLen, edgeStripWidth,
                new Vector3(0, sy * (half + surfaceOffset), sz * (half - edgeStripWidth / 2)),
                'x', sy > 0 ? -Math.PI / 2 : Math.PI / 2
            );
            meshes.push(yResult.mesh);
            outlines.push(yResult.outline);
            cubeGroup.add(yResult.mesh);
            cubeGroup.add(yResult.outline);

            // Strip on Z face - position at the edge where Y meets Z
            const zResult = createStripWithOutline(
                edgeLen, edgeStripWidth,
                new Vector3(0, sy * (half - edgeStripWidth / 2), sz * (half + surfaceOffset)),
                'x', sz > 0 ? 0 : Math.PI
            );
            meshes.push(zResult.mesh);
            outlines.push(zResult.outline);
            cubeGroup.add(zResult.mesh);
            cubeGroup.add(zResult.outline);

            edgeHighlights.push({ meshes, outlines, direction: new Vector3(0, sy, sz).normalize() });
        });

        // 4 edges parallel to Y axis (where X and Z faces meet)
        [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sx, sz]) => {
            const meshes: Mesh[] = [];
            const outlines: LineSegments2[] = [];
            const edgeLen = cubeSize - cornerStripSize * 2;

            // Strip on X face
            const xResult = createStripWithOutline(
                edgeStripWidth, edgeLen,
                new Vector3(sx * (half + surfaceOffset), 0, sz * (half - edgeStripWidth / 2)),
                'y', sx > 0 ? Math.PI / 2 : -Math.PI / 2
            );
            meshes.push(xResult.mesh);
            outlines.push(xResult.outline);
            cubeGroup.add(xResult.mesh);
            cubeGroup.add(xResult.outline);

            // Strip on Z face
            const zResult = createStripWithOutline(
                edgeStripWidth, edgeLen,
                new Vector3(sx * (half - edgeStripWidth / 2), 0, sz * (half + surfaceOffset)),
                'x', sz > 0 ? 0 : Math.PI
            );
            meshes.push(zResult.mesh);
            outlines.push(zResult.outline);
            cubeGroup.add(zResult.mesh);
            cubeGroup.add(zResult.outline);

            edgeHighlights.push({ meshes, outlines, direction: new Vector3(sx, 0, sz).normalize() });
        });

        // 4 edges parallel to Z axis (where X and Y faces meet)
        [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sx, sy]) => {
            const meshes: Mesh[] = [];
            const outlines: LineSegments2[] = [];
            const edgeLen = cubeSize - cornerStripSize * 2;

            // Strip on X face - strip runs along Z axis, narrow in Y
            const xResult = createStripWithOutline(
                edgeLen, edgeStripWidth,
                new Vector3(sx * (half + surfaceOffset), sy * (half - edgeStripWidth / 2), 0),
                'y', sx > 0 ? Math.PI / 2 : -Math.PI / 2
            );
            meshes.push(xResult.mesh);
            outlines.push(xResult.outline);
            cubeGroup.add(xResult.mesh);
            cubeGroup.add(xResult.outline);

            // Strip on Y face - strip runs along Z axis, narrow in X
            const yResult = createStripWithOutline(
                edgeStripWidth, edgeLen,
                new Vector3(sx * (half - edgeStripWidth / 2), sy * (half + surfaceOffset), 0),
                'x', sy > 0 ? -Math.PI / 2 : Math.PI / 2
            );
            meshes.push(yResult.mesh);
            outlines.push(yResult.outline);
            cubeGroup.add(yResult.mesh);
            cubeGroup.add(yResult.outline);

            edgeHighlights.push({ meshes, outlines, direction: new Vector3(sx, sy, 0).normalize() });
        });

        // Corner highlights - each corner has THREE small squares with outlines (one on each adjacent face)
        const cornerHighlights: { meshes: Mesh[]; outlines: LineSegments2[]; direction: Vector3 }[] = [];

        [[-1, 1], [1, 1], [-1, -1], [1, -1]].forEach(([sx, sy]) => {
            [[-1], [1]].forEach(([sz]) => {
                const meshes: Mesh[] = [];
                const outlines: LineSegments2[] = [];

                // Calculate corner position on each face
                // The square should have one corner at the cube corner
                const cornerOffset = half - cornerStripSize / 2;

                // Square on Z face (top or bottom) - at corner of the face
                const zResult = createStripWithOutline(
                    cornerStripSize, cornerStripSize,
                    new Vector3(
                        sx * cornerOffset,
                        sy * cornerOffset,
                        sz * (half + surfaceOffset)
                    ),
                    'x', sz > 0 ? 0 : Math.PI
                );
                meshes.push(zResult.mesh);
                outlines.push(zResult.outline);
                cubeGroup.add(zResult.mesh);
                cubeGroup.add(zResult.outline);

                // Square on Y face (front or back) - at corner of the face
                const yResult = createStripWithOutline(
                    cornerStripSize, cornerStripSize,
                    new Vector3(
                        sx * cornerOffset,
                        sy * (half + surfaceOffset),
                        sz * cornerOffset
                    ),
                    'x', sy > 0 ? -Math.PI / 2 : Math.PI / 2
                );
                meshes.push(yResult.mesh);
                outlines.push(yResult.outline);
                cubeGroup.add(yResult.mesh);
                cubeGroup.add(yResult.outline);

                // Square on X face (left or right) - at corner of the face
                const xResult = createStripWithOutline(
                    cornerStripSize, cornerStripSize,
                    new Vector3(
                        sx * (half + surfaceOffset),
                        sy * cornerOffset,
                        sz * cornerOffset
                    ),
                    'y', sx > 0 ? Math.PI / 2 : -Math.PI / 2
                );
                meshes.push(xResult.mesh);
                outlines.push(xResult.outline);
                cubeGroup.add(xResult.mesh);
                cubeGroup.add(xResult.outline);

                cornerHighlights.push({ meshes, outlines, direction: new Vector3(sx, sy, sz).normalize() });
            });
        });

        // Edge lines - 12 cube edges positioned on the flat face surfaces
        const edgeBaseColor = new Color('#a0ff00');
        const edgeBaseOpacity = 0.7;

        // Edge length ends where rounded corners begin
        const h = half - cornerRadius;
        // Small offset to prevent z-fighting with cube surface
        const edgeOffset = half + 0.003;

        // Create all 12 edges - positioned ON face surfaces, length limited to flat region
        const edgePositions: number[] = [
            // Bottom face edges (on Z = -edgeOffset surface)
            -h, -edgeOffset, -edgeOffset, h, -edgeOffset, -edgeOffset,  // front bottom (along X)
            -h, edgeOffset, -edgeOffset, h, edgeOffset, -edgeOffset,    // back bottom (along X)
            -edgeOffset, -h, -edgeOffset, -edgeOffset, h, -edgeOffset,  // left bottom (along Y)
            edgeOffset, -h, -edgeOffset, edgeOffset, h, -edgeOffset,    // right bottom (along Y)
            // Top face edges (on Z = +edgeOffset surface)
            -h, -edgeOffset, edgeOffset, h, -edgeOffset, edgeOffset,    // front top (along X)
            -h, edgeOffset, edgeOffset, h, edgeOffset, edgeOffset,      // back top (along X)
            -edgeOffset, -h, edgeOffset, -edgeOffset, h, edgeOffset,    // left top (along Y)
            edgeOffset, -h, edgeOffset, edgeOffset, h, edgeOffset,      // right top (along Y)
            // Vertical edges (along Z)
            -edgeOffset, -edgeOffset, -h, -edgeOffset, -edgeOffset, h,  // front-left
            edgeOffset, -edgeOffset, -h, edgeOffset, -edgeOffset, h,    // front-right
            -edgeOffset, edgeOffset, -h, -edgeOffset, edgeOffset, h,    // back-left
            edgeOffset, edgeOffset, -h, edgeOffset, edgeOffset, h       // back-right
        ];

        const lineGeometry = new LineSegmentsGeometry();
        lineGeometry.setPositions(edgePositions);

        const edgeMaterial = new LineMaterial({
            color: edgeBaseColor,
            transparent: true,
            opacity: edgeBaseOpacity,
            linewidth: 2,
            depthTest: true,
            depthWrite: false,
            resolution: new Vector2(window.innerWidth, window.innerHeight)
        });

        const edges = new LineSegments2(lineGeometry, edgeMaterial);
        edges.computeLineDistances();
        edges.renderOrder = 2;
        cubeGroup.add(edges);

        // Drop shadow under the cube - fixed position, doesn't rotate with cube
        const createShadowTexture = (isDark: boolean) => {
            const canvas = document.createElement('canvas');
            const size = 128;
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d')!;

            // Theme-aware shadow opacity - more visible in light mode
            const centerOpacity = isDark ? 0.4 : 0.35;
            const midOpacity = isDark ? 0.2 : 0.18;

            // Radial gradient for soft shadow
            const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
            gradient.addColorStop(0, `rgba(0, 0, 0, ${centerOpacity})`);
            gradient.addColorStop(0.5, `rgba(0, 0, 0, ${midOpacity})`);
            gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, size, size);

            const texture = new CanvasTexture(canvas);
            texture.needsUpdate = true;
            return texture;
        };

        let shadowTexture = createShadowTexture(currentThemeIsDark);
        const shadowGeometry = new PlaneGeometry(2.2, 2.2);
        const shadowMaterial = new MeshBasicMaterial({
            map: shadowTexture,
            transparent: true,
            opacity: currentThemeIsDark ? 0.7 : 0.8,
            depthWrite: false,
            depthTest: false
        });
        const shadow = new Mesh(shadowGeometry, shadowMaterial);
        // Position shadow in overlayGroup (doesn't rotate) - offset down and back
        shadow.position.set(0.15, -0.15, -0.5);
        shadow.renderOrder = -1;
        overlayGroup.add(shadow);

        // Click feedback animation state
        let clickScale = 1.0;
        let clickScaleTarget = 1.0;
        const clickScaleSpeed = 12; // How fast the scale animates
        const clickScaleBounce = 1.08; // Scale up to this value on click

        // Function to trigger click feedback
        const triggerClickFeedback = () => {
            clickScale = clickScaleBounce;
            clickScaleTarget = 1.0;
        };

        // Update shadow for theme changes
        const updateShadowTheme = (isDark: boolean) => {
            const oldTexture = shadowTexture;
            shadowTexture = createShadowTexture(isDark);
            shadowMaterial.map = shadowTexture;
            shadowMaterial.opacity = isDark ? 0.7 : 0.8;
            shadowMaterial.needsUpdate = true;
            oldTexture.dispose();
        };

        // Axis indicators colored lines only
        const axisLength = cubeSize; // Same length as cube edge
        const axisColors = {
            x: new Color('#ef4444'), // Red
            y: new Color('#22c55e'), // Green
            z: new Color('#3b82f6')  // Blue
        };

        // Create axis lines using Line2 for proper thickness
        const createAxisLine = (start: Vector3, end: Vector3, color: Color) => {
            const lineGeo = new LineSegmentsGeometry();
            lineGeo.setPositions([start.x, start.y, start.z, end.x, end.y, end.z]);
            const lineMat = new LineMaterial({
                color: color,
                linewidth: 2.5,
                depthTest: true,
                depthWrite: false,
                transparent: true,
                opacity: 1,
                resolution: new Vector2(window.innerWidth, window.innerHeight)
            });
            const line = new LineSegments2(lineGeo, lineMat);
            line.renderOrder = 4;
            return line;
        };

        // Axes originate from left-bottom-front corner of the cube
        // Coordinate system: +X=RIGHT, +Y=BACK, +Z=TOP
        const cornerOrigin = new Vector3(-half, -half, -half); // Left-front-bottom corner

        // X axis (red) - pointing right (+X)
        const xAxisLine = createAxisLine(
            cornerOrigin.clone(),
            new Vector3(cornerOrigin.x + axisLength, cornerOrigin.y, cornerOrigin.z),
            axisColors.x
        );
        cubeGroup.add(xAxisLine);

        // Y axis (green) - pointing back (+Y direction)
        const yAxisLine = createAxisLine(
            cornerOrigin.clone(),
            new Vector3(cornerOrigin.x, cornerOrigin.y + axisLength, cornerOrigin.z),
            axisColors.y
        );
        cubeGroup.add(yAxisLine);

        // Z axis (blue) - pointing up (+Z)
        const zAxisLine = createAxisLine(
            cornerOrigin.clone(),
            new Vector3(cornerOrigin.x, cornerOrigin.y, cornerOrigin.z + axisLength),
            axisColors.z
        );
        cubeGroup.add(zAxisLine);

        // Axis label creation helper
        const createAxisLabelTexture = (text: string, color: Color): CanvasTexture => {
            const canvas = document.createElement('canvas');
            const size = 128;
            canvas.width = size;
            canvas.height = size;

            const context = canvas.getContext('2d')!;
            context.clearRect(0, 0, size, size);
            context.font = '700 80px "SF Pro Display", sans-serif';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillStyle = `#${color.getHexString()}`;
            context.fillText(text, size / 2, size / 2);

            const texture = new CanvasTexture(canvas);
            texture.colorSpace = SRGBColorSpace;
            texture.generateMipmaps = true;
            texture.minFilter = LinearMipmapLinearFilter;
            texture.magFilter = LinearFilter;
            texture.anisotropy = 16;
            return texture;
        };

        const axisLabelSize = 0.25;
        const axisLabelGeometry = new PlaneGeometry(axisLabelSize, axisLabelSize);
        const axisLabelOffset = 0.18; // Offset from axis end

        const axisLabels: { mesh: Mesh; axis: 'x' | 'y' | 'z'; color: Color }[] = [];

        const createAxisLabel = (text: string, position: Vector3, color: Color, axis: 'x' | 'y' | 'z'): Mesh => {
            const texture = createAxisLabelTexture(text, color);
            const material = new MeshBasicMaterial({
                map: texture,
                transparent: true,
                opacity: 1,
                depthTest: false,
                depthWrite: false,
                toneMapped: false,
                side: DoubleSide
            });
            const mesh = new Mesh(axisLabelGeometry, material);
            mesh.position.copy(position);
            mesh.renderOrder = 5;
            axisLabels.push({ mesh, axis, color });
            cubeGroup.add(mesh);
            return mesh;
        };

        // Position labels at the end of each axis line
        createAxisLabel(
            'X',
            new Vector3(cornerOrigin.x + axisLength + axisLabelOffset, cornerOrigin.y, cornerOrigin.z),
            axisColors.x,
            'x'
        );
        createAxisLabel(
            'Y',
            new Vector3(cornerOrigin.x, cornerOrigin.y + axisLength + axisLabelOffset, cornerOrigin.z),
            axisColors.y,
            'y'
        );
        createAxisLabel(
            'Z',
            new Vector3(cornerOrigin.x, cornerOrigin.y, cornerOrigin.z + axisLength + axisLabelOffset),
            axisColors.z,
            'z'
        );

        // Update axis labels to always face the camera (billboard effect) and hide when behind cube
        const axisDirections: Record<'x' | 'y' | 'z', Vector3> = {
            x: new Vector3(1, 0, 0),
            y: new Vector3(0, 1, 0),
            z: new Vector3(0, 0, 1)
        };
        const axisLabelWorldDir = new Vector3();

        const updateAxisLabels = (): void => {
            axisLabels.forEach(({ mesh, axis }) => {
                // Make the label face the camera by copying inverse of cube rotation
                mesh.quaternion.copy(cubeGroup.quaternion).invert();

                // Check if this axis is pointing toward the camera or away
                // Transform the axis direction to world space
                axisLabelWorldDir.copy(axisDirections[axis]).applyQuaternion(cubeGroup.quaternion);

                // Dot product with view direction (0, 0, 1) - positive means facing camera
                const facing = axisLabelWorldDir.z;

                // Hide label if axis is pointing away from camera (behind the cube)
                mesh.visible = facing > -0.15;
            });
        };

        const labelMeshes: Record<string, Mesh> = {};
        const labelText: Record<string, string> = {
            posX: 'RIGHT',
            negX: 'LEFT',
            posY: 'BACK',
            negY: 'FRONT',
            posZ: 'TOP',
            negZ: 'BOTTOM'
        };

        const labelPlane = new PlaneGeometry(0.85, 0.85);
        const labelNormals: Record<string, Vector3> = {
            posX: new Vector3(1, 0, 0),
            negX: new Vector3(-1, 0, 0),
            posY: new Vector3(0, 1, 0),
            negY: new Vector3(0, -1, 0),
            posZ: new Vector3(0, 0, 1),
            negZ: new Vector3(0, 0, -1)
        };
        const labelViewDir = new Vector3(0, 0, 1);
        const labelNormalWorld = new Vector3();

        const createLabelTexture = (text: string, isDark: boolean) => {
            const canvas = document.createElement('canvas');
            const size = 256; // Higher resolution for crisp text
            canvas.width = size;
            canvas.height = size;

            const context = canvas.getContext('2d')!;
            context.clearRect(0, 0, size, size);
            // Scale font size proportionally to canvas size
            const scaledFont = options.font.replace(/(\d+)px/, (_: string, px: string) => `${parseInt(px) * 2}px`);
            context.font = scaledFont;
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            // Theme-aware label colors
            if (isDark) {
                context.fillStyle = '#e0e0e0';
                context.shadowColor = 'rgba(160, 255, 0, 0.3)';
                context.shadowBlur = 12;
            } else {
                context.fillStyle = '#404040';
                context.shadowColor = 'rgba(0, 0, 0, 0.2)';
                context.shadowBlur = 8;
            }
            context.fillText(text, size / 2, size / 2 + 2);

            const texture = new CanvasTexture(canvas);
            texture.colorSpace = SRGBColorSpace;
            texture.generateMipmaps = true;
            texture.minFilter = LinearMipmapLinearFilter;
            texture.magFilter = LinearFilter;
            texture.anisotropy = 16;
            return texture;
        };

        const createLabelMaterial = (text: string) =>
            new MeshBasicMaterial({
                map: createLabelTexture(text, currentThemeIsDark),
                transparent: true,
                opacity: 0.95,
                depthTest: false,
                depthWrite: false,
                toneMapped: false,
                side: DoubleSide
            });

        const addLabel = (key: string, text: string, position: Vector3, rotation: Vector3) => {
            const material = createLabelMaterial(text);
            const mesh = new Mesh(labelPlane, material);
            mesh.position.copy(position);
            mesh.rotation.set(rotation.x, rotation.y, rotation.z);
            mesh.renderOrder = 3;
            labelMeshes[key] = mesh;
            cubeGroup.add(mesh);
        };

        const offset = 0.48;
        // Static label rotations - text upright with top pointing to +Z (world up) where possible
        // For X faces: rotate around Y to face the direction, then Z to make text horizontal
        addLabel('posX', labelText['posX'], new Vector3(offset, 0, 0), new Vector3(0, Math.PI / 2, Math.PI / 2));
        addLabel('negX', labelText['negX'], new Vector3(-offset, 0, 0), new Vector3(0, -Math.PI / 2, -Math.PI / 2));
        // For Y faces: text up points to +Z
        addLabel('posY', labelText['posY'], new Vector3(0, offset, 0), new Vector3(-Math.PI / 2, 0, Math.PI));
        addLabel('negY', labelText['negY'], new Vector3(0, -offset, 0), new Vector3(Math.PI / 2, 0, 0));
        // For Z faces: text up points to -Y (toward front)
        addLabel('posZ', labelText['posZ'], new Vector3(0, 0, offset), new Vector3(0, 0, 0));
        addLabel('negZ', labelText['negZ'], new Vector3(0, 0, -offset), new Vector3(Math.PI, 0, 0));

        const controlTargets: Mesh[] = [];
        const faceTargets: Mesh[] = [];
        const edgeTargets: Mesh[] = [];
        const cornerTargets: Mesh[] = [];
        const controlButtons: Record<string, { mesh: Mesh; normal: CanvasTexture; hover: CanvasTexture }> = {};

        const createControlTexture = (icon: 'center' | 'fit' | 'iso', hovered: boolean, isDark: boolean) => {
            const canvas = document.createElement('canvas');
            const size = 256; // Higher resolution for crisp icons
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d')!;

            const center = size / 2;
            const radius = size * 0.46;
            ctx.clearRect(0, 0, size, size);

            // Theme-aware button background
            if (isDark) {
                ctx.fillStyle = hovered ? 'rgba(30, 30, 30, 0.95)' : 'rgba(20, 20, 20, 0.85)';
            } else {
                ctx.fillStyle = hovered ? 'rgba(240, 240, 240, 0.95)' : 'rgba(230, 230, 230, 0.85)';
            }
            ctx.beginPath();
            ctx.arc(center, center, radius, 0, Math.PI * 2);
            ctx.fill();

            // Border - theme-aware hover effect
            if (isDark) {
                ctx.strokeStyle = hovered ? 'rgba(160, 255, 0, 0.6)' : 'rgba(160, 255, 0, 0.2)';
            } else {
                ctx.strokeStyle = hovered ? 'rgba(80, 80, 80, 0.7)' : 'rgba(180, 180, 180, 0.5)';
            }
            ctx.lineWidth = hovered ? 5 : 3;
            ctx.stroke();

            // Icon color - theme-aware hover effect
            if (isDark) {
                ctx.strokeStyle = hovered ? '#a0ff00' : 'rgba(200, 200, 200, 0.85)';
            } else {
                ctx.strokeStyle = hovered ? '#505050' : 'rgba(80, 80, 80, 0.85)';
            }
            ctx.lineWidth = 8;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            if (icon === 'center') {
                const ring = size * 0.14;
                ctx.beginPath();
                ctx.arc(center, center, ring, 0, Math.PI * 2);
                ctx.stroke();

                const tick = size * 0.18;
                ctx.beginPath();
                ctx.moveTo(center - tick, center);
                ctx.lineTo(center - ring * 1.1, center);
                ctx.moveTo(center + ring * 1.1, center);
                ctx.lineTo(center + tick, center);
                ctx.moveTo(center, center - tick);
                ctx.lineTo(center, center - ring * 1.1);
                ctx.moveTo(center, center + ring * 1.1);
                ctx.lineTo(center, center + tick);
                ctx.stroke();
            } else if (icon === 'fit') {
                const inset = size * 0.28;
                const corner = size * 0.16;
                ctx.beginPath();
                ctx.moveTo(inset, inset + corner);
                ctx.lineTo(inset, inset);
                ctx.lineTo(inset + corner, inset);
                ctx.moveTo(size - inset - corner, inset);
                ctx.lineTo(size - inset, inset);
                ctx.lineTo(size - inset, inset + corner);
                ctx.moveTo(inset, size - inset - corner);
                ctx.lineTo(inset, size - inset);
                ctx.lineTo(inset + corner, size - inset);
                ctx.moveTo(size - inset - corner, size - inset);
                ctx.lineTo(size - inset, size - inset);
                ctx.lineTo(size - inset, size - inset - corner);
                ctx.stroke();
            } else if (icon === 'iso') {
                const sizeBox = size * 0.28;
                const isoOffset = size * 0.12;
                // Center the combined shape (front box + offset for back box)
                const x = center - (sizeBox + isoOffset) / 2;
                const y = center - (sizeBox - isoOffset) / 2;

                // Front face
                ctx.beginPath();
                ctx.rect(x, y, sizeBox, sizeBox);
                ctx.stroke();

                // Back face and connecting lines
                ctx.beginPath();
                ctx.rect(x + isoOffset, y - isoOffset, sizeBox, sizeBox);
                ctx.moveTo(x, y);
                ctx.lineTo(x + isoOffset, y - isoOffset);
                ctx.moveTo(x + sizeBox, y);
                ctx.lineTo(x + sizeBox + isoOffset, y - isoOffset);
                ctx.moveTo(x + sizeBox, y + sizeBox);
                ctx.lineTo(x + sizeBox + isoOffset, y + sizeBox - isoOffset);
                ctx.stroke();
            }

            const texture = new CanvasTexture(canvas);
            texture.colorSpace = SRGBColorSpace;
            texture.generateMipmaps = true;
            texture.minFilter = LinearMipmapLinearFilter;
            texture.magFilter = LinearFilter;
            texture.anisotropy = 16;
            return texture;
        };

        type ArrowType = 'rotUp' | 'rotDown' | 'rotLeft' | 'rotRight' | 'rollCW' | 'rollCCW';

        const createArrowTexture = (type: ArrowType, hovered: boolean, isDark: boolean): CanvasTexture => {
            const canvas = document.createElement('canvas');
            const size = 256;
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d')!;
            const center = size / 2;

            ctx.clearRect(0, 0, size, size);

            // Arrow color - solid and visible like FreeCAD
            if (hovered) {
                ctx.fillStyle = '#a0ff00';
                ctx.strokeStyle = '#a0ff00';
            } else if (isDark) {
                ctx.fillStyle = 'rgba(200, 200, 200, 0.75)';
                ctx.strokeStyle = 'rgba(200, 200, 200, 0.75)';
            } else {
                ctx.fillStyle = 'rgba(80, 80, 80, 0.6)';
                ctx.strokeStyle = 'rgba(80, 80, 80, 0.6)';
            }

            if (type === 'rollCW' || type === 'rollCCW') {
                // Filled thick arc with arrowhead — FreeCAD style
                const isCW = type === 'rollCW';
                const outerRadius = size * 0.4;
                const innerRadius = size * 0.28;
                const midRadius = (outerRadius + innerRadius) / 2;
                const arcThickness = outerRadius - innerRadius;

                // Arc sweep: ~120 degrees
                const sweepStart = isCW ? -Math.PI * 0.82 : -Math.PI * 0.18;
                const sweepEnd = isCW ? -Math.PI * 0.28 : -Math.PI * 0.72;

                // Arrowhead tip extends well beyond the arc end
                const arrowTipAngle = isCW ? sweepEnd + 0.45 : sweepEnd - 0.45;

                ctx.beginPath();

                // Outer arc (forward direction) — stop short to leave room for arrowhead
                const arrowStartAngle = isCW ? sweepEnd - 0.08 : sweepEnd + 0.08;
                ctx.arc(center, center, outerRadius, sweepStart, arrowStartAngle, !isCW);

                // Arrowhead: outer wing -> tip -> inner wing
                const outerWingX = center + Math.cos(arrowStartAngle) * (outerRadius + arcThickness * 0.75);
                const outerWingY = center + Math.sin(arrowStartAngle) * (outerRadius + arcThickness * 0.75);
                ctx.lineTo(outerWingX, outerWingY);

                const tipX = center + Math.cos(arrowTipAngle) * midRadius;
                const tipY = center + Math.sin(arrowTipAngle) * midRadius;
                ctx.lineTo(tipX, tipY);

                const innerWingX = center + Math.cos(arrowStartAngle) * (innerRadius - arcThickness * 0.75);
                const innerWingY = center + Math.sin(arrowStartAngle) * (innerRadius - arcThickness * 0.75);
                ctx.lineTo(innerWingX, innerWingY);

                // Inner arc (reverse direction back to start)
                ctx.arc(center, center, innerRadius, sweepEnd, sweepStart, isCW);

                ctx.closePath();
                ctx.fill();
            } else {
                // Cardinal triangle arrow - solid and prominent like FreeCAD
                const triSize = size * 0.38;
                ctx.beginPath();

                switch (type) {
                    case 'rotUp':
                        ctx.moveTo(center, center - triSize);
                        ctx.lineTo(center - triSize * 0.65, center + triSize * 0.3);
                        ctx.lineTo(center + triSize * 0.65, center + triSize * 0.3);
                        break;
                    case 'rotDown':
                        ctx.moveTo(center, center + triSize);
                        ctx.lineTo(center - triSize * 0.65, center - triSize * 0.3);
                        ctx.lineTo(center + triSize * 0.65, center - triSize * 0.3);
                        break;
                    case 'rotLeft':
                        ctx.moveTo(center - triSize, center);
                        ctx.lineTo(center + triSize * 0.3, center - triSize * 0.65);
                        ctx.lineTo(center + triSize * 0.3, center + triSize * 0.65);
                        break;
                    case 'rotRight':
                        ctx.moveTo(center + triSize, center);
                        ctx.lineTo(center - triSize * 0.3, center - triSize * 0.65);
                        ctx.lineTo(center - triSize * 0.3, center + triSize * 0.65);
                        break;
                }

                ctx.closePath();
                ctx.fill();
            }

            const texture = new CanvasTexture(canvas);
            texture.colorSpace = SRGBColorSpace;
            texture.generateMipmaps = true;
            texture.minFilter = LinearMipmapLinearFilter;
            texture.magFilter = LinearFilter;
            texture.anisotropy = 16;
            return texture;
        };

        const controlPlane = new PlaneGeometry(0.6, 0.6);
        const addControlButton = (key: 'center' | 'fit' | 'iso', position: Vector3) => {
            const normal = createControlTexture(key, false, currentThemeIsDark);
            const hover = createControlTexture(key, true, currentThemeIsDark);
            const material = new MeshBasicMaterial({
                map: normal,
                transparent: true,
                depthTest: false,
                depthWrite: false,
                toneMapped: false
            });
            const mesh = new Mesh(controlPlane, material);
            mesh.position.copy(position);
            mesh.renderOrder = 4;
            mesh.userData['viewCubeControl'] = key;
            controlTargets.push(mesh);
            overlayGroup.add(mesh);
            controlButtons[key] = { mesh, normal, hover };
        };

        const controlZ = 1.15;
        const controlOffset = 1.1;
        addControlButton('center', new Vector3(-controlOffset, controlOffset, controlZ));
        addControlButton('fit', new Vector3(controlOffset, controlOffset, controlZ));
        addControlButton('iso', new Vector3(-controlOffset, -controlOffset, controlZ));

        const arrowButtons: Partial<Record<ArrowType, { mesh: Mesh; normal: CanvasTexture; hover: CanvasTexture }>> = {};

        const arrowPlane = new PlaneGeometry(0.55, 0.55);
        const rollArrowPlane = new PlaneGeometry(0.65, 0.65);

        const addArrowButton = (key: ArrowType, position: Vector3) => {
            const isRoll = key === 'rollCW' || key === 'rollCCW';
            const normal = createArrowTexture(key, false, currentThemeIsDark);
            const hover = createArrowTexture(key, true, currentThemeIsDark);
            const material = new MeshBasicMaterial({
                map: normal,
                transparent: true,
                depthTest: false,
                depthWrite: false,
                toneMapped: false
            });
            const mesh = new Mesh(isRoll ? rollArrowPlane : arrowPlane, material);
            mesh.position.copy(position);
            mesh.renderOrder = 4;
            mesh.userData['viewCubeControl'] = key;
            controlTargets.push(mesh);
            overlayGroup.add(mesh);
            arrowButtons[key] = { mesh, normal, hover };
        };

        // Arrow layout:  ↶ ▲ ↷ above buttons,  ◄ CUBE ► sides,  ▼ bottom
        const arrowZ = 1.15;
        const arrowOuterOffset = 1.1;
        addArrowButton('rotUp', new Vector3(0, arrowOuterOffset, arrowZ));
        addArrowButton('rotDown', new Vector3(0, -arrowOuterOffset, arrowZ));
        addArrowButton('rotLeft', new Vector3(-arrowOuterOffset, 0, arrowZ));
        addArrowButton('rotRight', new Vector3(arrowOuterOffset, 0, arrowZ));
        addArrowButton('rollCCW', new Vector3(-0.55, arrowOuterOffset, arrowZ));
        addArrowButton('rollCW', new Vector3(0.55, arrowOuterOffset, arrowZ));

        const pickTargets: Mesh[] = [];
        const pickMaterial = new MeshBasicMaterial({
            color: new Color('#ffffff'),
            transparent: true,
            opacity: 0,
            depthTest: false,
            side: DoubleSide
        });

        const addPickTarget = (mesh: Mesh, direction: Vector3, type: 'face' | 'edge' | 'corner') => {
            mesh.userData['viewCubeDirection'] = direction;
            mesh.userData['viewCubeType'] = type;
            pickTargets.push(mesh);
            if (type === 'face') {
                faceTargets.push(mesh);
            } else if (type === 'edge') {
                edgeTargets.push(mesh);
            } else {
                cornerTargets.push(mesh);
            }
            cubeGroup.add(mesh);
        };

        // Pick target dimensions for simple cube
        // The cube is divided into face, edge, and corner zones:
        // - Face: Central region of each face (excludes edges/corners)
        // - Edge: Thin strips along each edge (excludes corners)
        // - Corner: Small cubes at each corner

        const pickZoneInset = 0.15; // How much edge/corner zones extend into face
        const facePickSize = cubeSize - pickZoneInset * 2; // Central face region
        const edgePickWidth = pickZoneInset; // Width of edge pick zone
        const edgePickLength = cubeSize - pickZoneInset * 2; // Length excludes corners
        const cornerPickSize = pickZoneInset; // Corner cube size
        const pickDepth = 0.12; // Depth for pick targets (slightly outside surface)

        const addFaceTarget = (direction: Vector3) => {
            const isX = Math.abs(direction.x) > 0.5;
            const isY = Math.abs(direction.y) > 0.5;
            const size = new Vector3(
                isX ? pickDepth : facePickSize,
                isY ? pickDepth : facePickSize,
                (!isX && !isY) ? pickDepth : facePickSize
            );
            const mesh = new Mesh(new BoxGeometry(size.x, size.y, size.z), pickMaterial);
            mesh.position.copy(direction).multiplyScalar(half);
            addPickTarget(mesh, direction.clone(), 'face');
        };

        addFaceTarget(new Vector3(1, 0, 0));
        addFaceTarget(new Vector3(-1, 0, 0));
        addFaceTarget(new Vector3(0, 1, 0));
        addFaceTarget(new Vector3(0, -1, 0));
        addFaceTarget(new Vector3(0, 0, 1));
        addFaceTarget(new Vector3(0, 0, -1));

        // Edge pick targets - thin strips along each edge (excluding corner zones)
        // Each edge is positioned at the intersection of two faces
        const edgePickCenter = half - edgePickWidth / 2; // Center of edge pick zone

        // Edges parallel to X axis (4 edges)
        [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sy, sz]) => {
            const mesh = new Mesh(
                new BoxGeometry(edgePickLength, edgePickWidth, edgePickWidth),
                pickMaterial
            );
            mesh.position.set(0, sy * edgePickCenter, sz * edgePickCenter);
            const dir = new Vector3(0, sy, sz).normalize();
            addPickTarget(mesh, dir, 'edge');
        });

        // Edges parallel to Y axis (4 edges)
        [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sx, sz]) => {
            const mesh = new Mesh(
                new BoxGeometry(edgePickWidth, edgePickLength, edgePickWidth),
                pickMaterial
            );
            mesh.position.set(sx * edgePickCenter, 0, sz * edgePickCenter);
            const dir = new Vector3(sx, 0, sz).normalize();
            addPickTarget(mesh, dir, 'edge');
        });

        // Edges parallel to Z axis (4 edges)
        [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sx, sy]) => {
            const mesh = new Mesh(
                new BoxGeometry(edgePickWidth, edgePickWidth, edgePickLength),
                pickMaterial
            );
            mesh.position.set(sx * edgePickCenter, sy * edgePickCenter, 0);
            const dir = new Vector3(sx, sy, 0).normalize();
            addPickTarget(mesh, dir, 'edge');
        });

        // Corner pick targets - small cubes at each corner
        const cornerPickCenter = half - cornerPickSize / 2;
        const cornerSigns = [-1, 1];
        cornerSigns.forEach((x) => {
            cornerSigns.forEach((y) => {
                cornerSigns.forEach((z) => {
                    const dir = new Vector3(x, y, z).normalize();
                    const mesh = new Mesh(
                        new BoxGeometry(cornerPickSize, cornerPickSize, cornerPickSize),
                        pickMaterial
                    );
                    mesh.position.set(x * cornerPickCenter, y * cornerPickCenter, z * cornerPickCenter);
                    addPickTarget(mesh, dir, 'corner');
                });
            });
        });

        let dragging = false;
        let dragPointerId: number | null = null;
        let activeControl: Mesh | null = null;
        let controlPointerId: number | null = null;
        let dragMoved = false;
        const dragStart = new Vector2();
        const dragLast = new Vector2();

        let hoveredDirection: Vector3 | null = null;
        let hoveredType: 'face' | 'edge' | 'corner' | null = null;
        let hoverTransition = 0; // 0-1 for enter/exit animation
        let pulseTime = 0; // Continuous time for pulse animation
        let isHovering = false;

        // Animation parameters
        const hoverAnimDuration = 0.12; // Quick pop-in animation
        const pulseSpeed = 4.0; // Gentle pulse speed

        const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

        const applyHoverState = (delta: number) => {
            // Update hover transition (0 to 1)
            if (isHovering) {
                hoverTransition = Math.min(hoverTransition + delta / hoverAnimDuration, 1);
                pulseTime += delta * pulseSpeed; // Continue pulse while hovering
            } else {
                hoverTransition = Math.max(hoverTransition - delta / (hoverAnimDuration * 2), 0);
                pulseTime = 0; // Reset pulse when not hovering
            }

            // Calculate animation values
            const hoverProgress = easeOutCubic(hoverTransition);
            const pulse = 0.5 - 0.5 * Math.cos(pulseTime);

            // Reset cube material to base state
            cubeMaterial.color.copy(faceBaseColor);
            edgeMaterial.color.copy(edgeBaseColor);
            edgeMaterial.opacity = edgeBaseOpacity;

            // Hide all face, edge and corner highlights
            faceHighlights.forEach(({ mesh, outline }) => {
                mesh.visible = false;
                (mesh.material as MeshBasicMaterial).opacity = 0;
                outline.visible = false;
                (outline.material as LineMaterial).opacity = 0;
            });
            edgeHighlights.forEach(({ meshes, outlines }) => {
                meshes.forEach((mesh) => {
                    mesh.visible = false;
                    (mesh.material as MeshBasicMaterial).opacity = 0;
                });
                outlines.forEach((outline) => {
                    outline.visible = false;
                    (outline.material as LineMaterial).opacity = 0;
                });
            });
            cornerHighlights.forEach(({ meshes, outlines }) => {
                meshes.forEach((mesh) => {
                    mesh.visible = false;
                    (mesh.material as MeshBasicMaterial).opacity = 0;
                });
                outlines.forEach((outline) => {
                    outline.visible = false;
                    (outline.material as LineMaterial).opacity = 0;
                });
            });

            if (!hoveredDirection || !hoveredType || hoverProgress <= 0.01) return;

            // Brighten wireframe edges with green tint when hovering
            edgeMaterial.color.copy(edgeBaseColor).lerp(highlightColor, 0.3 * hoverProgress);
            edgeMaterial.opacity = edgeBaseOpacity + 0.35 * hoverProgress;

            if (hoveredType === 'face') {
                // Show central rectangle highlight with outline
                const hoveredFace = faceHighlights.find(({ direction }) => {
                    return direction.dot(hoveredDirection!) > 0.99;
                });
                if (hoveredFace) {
                    const fillOpacity = 0.5 * hoverProgress + pulse * 0.1 * hoverProgress;
                    const outlineOpacity = 0.9 * hoverProgress;

                    hoveredFace.mesh.visible = true;
                    (hoveredFace.mesh.material as MeshBasicMaterial).opacity = fillOpacity;

                    hoveredFace.outline.visible = true;
                    (hoveredFace.outline.material as LineMaterial).opacity = outlineOpacity;
                }
            } else if (hoveredType === 'edge') {
                // Find matching edge highlight by direction (must be same direction, not opposite)
                const hoveredEdge = edgeHighlights.find(({ direction }) => {
                    return direction.dot(hoveredDirection!) > 0.99;
                });
                if (hoveredEdge) {
                    const fillOpacity = 0.5 * hoverProgress + pulse * 0.1 * hoverProgress;
                    const outlineOpacity = 0.9 * hoverProgress;
                    hoveredEdge.meshes.forEach((mesh) => {
                        mesh.visible = true;
                        (mesh.material as MeshBasicMaterial).opacity = fillOpacity;
                    });
                    hoveredEdge.outlines.forEach((outline) => {
                        outline.visible = true;
                        (outline.material as LineMaterial).opacity = outlineOpacity;
                    });
                }
            } else if (hoveredType === 'corner') {
                // Find matching corner highlight by direction (must be same direction, not opposite)
                const hoveredCorner = cornerHighlights.find(({ direction }) => {
                    return direction.dot(hoveredDirection!) > 0.99;
                });
                if (hoveredCorner) {
                    const fillOpacity = 0.5 * hoverProgress + pulse * 0.1 * hoverProgress;
                    const outlineOpacity = 0.9 * hoverProgress;
                    hoveredCorner.meshes.forEach((mesh) => {
                        mesh.visible = true;
                        (mesh.material as MeshBasicMaterial).opacity = fillOpacity;
                    });
                    hoveredCorner.outlines.forEach((outline) => {
                        outline.visible = true;
                        (outline.material as LineMaterial).opacity = outlineOpacity;
                    });
                }
            }
        };

        const setHoverState = (hit: Mesh | null, direction?: Vector3 | null, type?: 'face' | 'edge' | 'corner' | null) => {
            const newDirection = direction !== undefined ? direction : (hit?.userData?.['viewCubeDirection'] as Vector3 | undefined) || null;
            const newType = type !== undefined ? type : (hit?.userData?.['viewCubeType'] as 'face' | 'edge' | 'corner' | undefined) || null;

            const directionChanged = !newDirection && hoveredDirection ||
                                     newDirection && !hoveredDirection ||
                                     (newDirection && hoveredDirection && !newDirection.equals(hoveredDirection));

            isHovering = !!(newDirection && newType);

            // Reset animation on direction change for snappy feedback
            if (directionChanged && isHovering) {
                hoverTransition = 0;
                pulseTime = 0;
            }

            hoveredDirection = newDirection;
            hoveredType = newType;
        };

        const updateLabelOrientation = () => {
            // Only handle visibility - hide labels on back-facing sides
            // Labels are static and don't rotate to face camera
            Object.entries(labelMeshes).forEach(([key, mesh]) => {
                const normal = labelNormals[key];
                if (!normal) return;
                labelNormalWorld.copy(normal).applyQuaternion(cubeGroup.quaternion).normalize();
                const facing = labelNormalWorld.dot(labelViewDir);
                mesh.visible = facing > 0.05;
            });

            // Axis labels also don't billboard - they stay static
            // (visibility is always on since they're at corners)
        };

        const updateCubeOrientation = () => {
            cubeGroup.quaternion.copy(camera.quaternion).invert();
            cubeGroup.updateMatrixWorld();
            updateLabelOrientation();
            updateAxisLabels();
        };

        const updateMouse = (event: PointerEvent) => {
            const rect = viewHelperContainer.getBoundingClientRect();
            const width = rect.width - containerPadding * 2;
            const height = rect.height - containerPadding * 2;
            if (width <= 0 || height <= 0) return false;

            const left = rect.left + containerPadding;
            const top = rect.top + containerPadding;
            const right = left + width;
            const bottom = top + height;

            if (
                event.clientX < left ||
                event.clientX > right ||
                event.clientY < top ||
                event.clientY > bottom
            ) {
                return false;
            }

            mouse.x = ((event.clientX - left) / width) * 2 - 1;
            mouse.y = -((event.clientY - top) / height) * 2 + 1;
            return true;
        };

        const updateRaycaster = (event: PointerEvent) => {
            if (!updateMouse(event)) return false;
            updateCubeOrientation();
            raycaster.setFromCamera(mouse, orthoCamera);
            return true;
        };

        type CubeHitResult = {
            mesh: Mesh;
            point: Vector3;
            distance: number;
            direction: Vector3;
            type: 'face' | 'edge' | 'corner';
        };

        const getCubeHit = (): CubeHitResult | null => {
            // Single raycast against all pick targets (all are BoxGeometry now)
            // Returns closest hit - no front-face check needed since boxes have volume
            const hits = raycaster.intersectObjects(pickTargets, false);

            if (hits.length === 0) return null;

            const hit = hits[0];
            const mesh = hit.object as Mesh;
            const type = mesh.userData['viewCubeType'] as 'face' | 'edge' | 'corner';
            const direction = mesh.userData['viewCubeDirection'] as Vector3;

            return {
                mesh,
                point: hit.point.clone(),
                distance: hit.distance,
                direction,
                type
            };
        };

        const getPick = (event: PointerEvent): { mesh: Mesh; hit: CubeHitResult | null } | null => {
            if (!updateRaycaster(event)) return null;

            const controlHit = raycaster.intersectObjects(controlTargets, false);
            if (controlHit.length) {
                return { mesh: controlHit[0].object as Mesh, hit: null };
            }

            const cubeHit = getCubeHit();
            if (cubeHit) {
                return { mesh: cubeHit.mesh, hit: cubeHit };
            }

            return null;
        };

        const getTargetUp = (viewDirection: Vector3): Vector3 => {
            // When looking along Z axis (top/bottom), use Y as up
            // Otherwise use Z (world up)
            return Math.abs(viewDirection.z) > 0.92 ? altUp.clone() : worldUp.clone();
        };

        const rotateCameraToDirection = (direction: Vector3, targetPoint?: Vector3) => {
            engine.clearOrbitPivot();

            const lookAtPoint = targetPoint || controls.target.clone();
            const distance = camera.position.distanceTo(controls.target);

            // Current eye vector (camera position relative to target)
            const startEye = new Vector3().subVectors(camera.position, controls.target);
            const startUp = camera.up.clone();

            // Target eye vector
            const targetEye = direction.clone().normalize().multiplyScalar(distance);
            const targetUp = getTargetUp(direction.clone().normalize().negate());

            // Calculate the rotation quaternion from start to target eye direction
            const startDir = startEye.clone().normalize();
            const targetDir = targetEye.clone().normalize();

            // Create quaternion representing rotation from start to target
            const rotationQuat = new Quaternion();
            rotationQuat.setFromUnitVectors(startDir, targetDir);

            // Also tween the controls target if it's changing
            const startTarget = controls.target.clone();
            const targetChanging = !lookAtPoint.equals(startTarget);

            // Interpolation state
            const state = { t: 0 };

            new TWEEN.Tween(state)
                .to({ t: 1 }, 280)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => {
                    const t = state.t;

                    // Spherical interpolation of eye vector using quaternion
                    const currentQuat = new Quaternion().slerp(rotationQuat, t);
                    const currentEye = startEye.clone().applyQuaternion(currentQuat);

                    // Interpolate target position if changing
                    const currentTarget = targetChanging
                        ? startTarget.clone().lerp(lookAtPoint, t)
                        : lookAtPoint;

                    // Update camera position (target + eye)
                    camera.position.copy(currentTarget).add(currentEye);

                    // Smooth up vector interpolation
                    camera.up.copy(startUp).lerp(targetUp, t).normalize();

                    // Update controls target
                    controls.target.copy(currentTarget);

                    camera.lookAt(currentTarget);
                    controls.update();
                })
                .start();
        };

        // Quaternion-based rotation (like TrackballControls) - no gimbal lock
        const rotateAxis = new Vector3();
        const rotateQuat = new Quaternion();
        const eyeDir = new Vector3();
        const upDir = new Vector3();
        const sideDir = new Vector3();
        const moveDir = new Vector3();

        const rotateCameraByDrag = (dx: number, dy: number) => {
            const rect = viewHelperContainer.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;

            // Normalize mouse movement
            const moveX = dx / rect.width;
            const moveY = dy / rect.height;

            // Get eye vector (camera position relative to target)
            const eye = camera.position.clone().sub(controls.target);

            // Calculate camera directions
            eyeDir.copy(eye).normalize();
            upDir.copy(camera.up).normalize();
            sideDir.crossVectors(upDir, eyeDir).normalize();

            // Build movement direction in camera space
            // Scale sideways by X movement, up by Y movement (negated for natural feel)
            upDir.setLength(-moveY);
            sideDir.setLength(moveX);
            moveDir.copy(upDir).add(sideDir);

            const angle = moveDir.length() * Math.PI * 1.1;
            if (angle < 0.0001) return;

            // Rotation axis is perpendicular to both movement and eye direction
            rotateAxis.crossVectors(moveDir, eye).normalize();

            // Create rotation quaternion
            rotateQuat.setFromAxisAngle(rotateAxis, angle);

            // Apply rotation to eye vector and camera up
            eye.applyQuaternion(rotateQuat);
            camera.up.applyQuaternion(rotateQuat);

            // Update camera position
            camera.position.copy(controls.target).add(eye);
            camera.lookAt(controls.target);
            controls.update();
        };

        const rotateCameraByAngle = (type: ArrowType): void => {
            engine.clearOrbitPivot();

            const angle = Math.PI / 4; // 45 degrees
            const eye = camera.position.clone().sub(controls.target);
            const startEye = eye.clone();
            const startUp = camera.up.clone();

            const axis = new Vector3();

            if (type === 'rotLeft' || type === 'rotRight') {
                // Orbit horizontally around world up (Z axis)
                axis.copy(worldUp);
                if (type === 'rotLeft') axis.negate();
            } else if (type === 'rotUp' || type === 'rotDown') {
                // Orbit vertically around camera side vector
                const eyeDir = eye.clone().normalize();
                const upDir = camera.up.clone().normalize();
                axis.crossVectors(upDir, eyeDir).normalize();
                if (type === 'rotDown') axis.negate();
            } else {
                // Roll around eye vector (camera look direction)
                axis.copy(eye).normalize();
                if (type === 'rollCCW') axis.negate();
            }

            const targetQuat = new Quaternion().setFromAxisAngle(axis, angle);
            const targetUp = startUp.clone().applyQuaternion(targetQuat).normalize();

            const state = { t: 0 };

            new TWEEN.Tween(state)
                .to({ t: 1 }, 280)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => {
                    const t = state.t;
                    const currentQuat = new Quaternion().slerp(targetQuat, t);
                    const currentEye = startEye.clone().applyQuaternion(currentQuat);

                    camera.position.copy(controls.target).add(currentEye);
                    camera.up.copy(startUp).lerp(targetUp, t).normalize();
                    camera.lookAt(controls.target);
                    controls.update();
                })
                .start();
        };

        this.handlePointerDown = (event: PointerEvent) => {
            if (event.button !== 0) return false;

            const pick = getPick(event);
            if (!pick) return false;

            const controlId = pick.mesh.userData?.['viewCubeControl'] as string | undefined;
            if (controlId) {
                activeControl = pick.mesh;
                controlPointerId = event.pointerId;
                dragMoved = false;
                dragStart.set(event.clientX, event.clientY);
                viewHelperContainer.setPointerCapture?.(event.pointerId);
                return true;
            }

            dragPointerId = event.pointerId;
            dragging = true;
            dragMoved = false;
            dragStart.set(event.clientX, event.clientY);
            dragLast.copy(dragStart);
            viewHelperContainer.setPointerCapture?.(event.pointerId);
            return true;
        };

        this.handlePointerMove = (event: PointerEvent) => {
            if (activeControl && controlPointerId === event.pointerId) {
                if (dragStart.distanceTo(new Vector2(event.clientX, event.clientY)) > 3) {
                    dragMoved = true;
                }
                return true;
            }

            if (dragging && dragPointerId === event.pointerId) {
                const dx = event.clientX - dragLast.x;
                const dy = event.clientY - dragLast.y;
                if (!dragMoved && dragStart.distanceTo(new Vector2(event.clientX, event.clientY)) > 3) {
                    dragMoved = true;
                    // Show the temporary pivot when rotation starts
                    engine?.showTemporaryPivot();
                }
                if (dx !== 0 || dy !== 0) {
                    rotateCameraByDrag(dx, dy);
                }
                dragLast.set(event.clientX, event.clientY);
                return true;
            }

            if (!updateRaycaster(event)) {
                setHoverState(null);
                viewHelperContainer.style.cursor = 'default';
                return false;
            }

            const controlHit = raycaster.intersectObjects(controlTargets, false);
            const controlMesh = (controlHit[0]?.object as Mesh) || null;

            [...Object.values(controlButtons), ...Object.values(arrowButtons)].forEach((button) => {
                const isHover = button.mesh === controlMesh;
                const material = button.mesh.material as MeshBasicMaterial;
                const nextMap = isHover ? button.hover : button.normal;
                if (material.map !== nextMap) {
                    material.map = nextMap;
                    material.needsUpdate = true;
                }
            });

            const cubeHit = controlMesh ? null : getCubeHit();
            if (cubeHit) {
                setHoverState(cubeHit.mesh, cubeHit.direction, cubeHit.type);
            } else {
                setHoverState(null);
            }

            const hasHover = !!controlMesh || !!cubeHit;
            viewHelperContainer.style.cursor = hasHover ? 'pointer' : 'default';
            return hasHover;
        };

        this.handlePointerUp = (event: PointerEvent) => {
            if (activeControl && controlPointerId === event.pointerId) {
                viewHelperContainer.releasePointerCapture?.(event.pointerId);
                const control = activeControl.userData?.['viewCubeControl'] as 'center' | 'fit' | 'iso' | ArrowType;
                activeControl = null;
                controlPointerId = null;
                if (!dragMoved) {
                    // Trigger click feedback for control buttons too
                    triggerClickFeedback();

                    if (control === 'center') {
                        this.centerCameraOnObjectOrthographic(engine?.group!);
                    } else if (control === 'fit') {
                        this.fitCameraToObject(engine?.group!, 1);
                    } else if (control === 'iso') {
                        this.fitCameraToGroupAndSetIsometricOrthographic(engine?.group!, 1);
                    } else if (
                        control === 'rotUp' || control === 'rotDown' ||
                        control === 'rotLeft' || control === 'rotRight' ||
                        control === 'rollCW' || control === 'rollCCW'
                    ) {
                        rotateCameraByAngle(control);
                    }
                }
                return true;
            }

            if (!dragging || dragPointerId !== event.pointerId) return false;

            viewHelperContainer.releasePointerCapture?.(event.pointerId);
            dragging = false;
            dragPointerId = null;

            // Hide the temporary pivot when rotation ends
            if (dragMoved) {
                engine?.hideTemporaryPivot();
            }

            if (!dragMoved) {
                if (updateRaycaster(event)) {
                    const cubeHit = getCubeHit();
                    if (cubeHit) {
                        // Trigger click feedback animation
                        triggerClickFeedback();

                        const direction = cubeHit.direction;
                        if (cubeHit.type === 'face') {
                            const axis =
                                direction.x > 0
                                    ? 'posX'
                                    : direction.x < 0
                                      ? 'negX'
                                      : direction.y > 0
                                        ? 'posY'
                                        : direction.y < 0
                                          ? 'negY'
                                          : direction.z > 0
                                            ? 'posZ'
                                            : 'negZ';
                            this.rotateCameraToSide(axis);
                        } else {
                            rotateCameraToDirection(direction);
                        }
                    }
                }
            }

            return true;
        };

        this.handlePointerLeave = () => {
            if (dragging) return;
            setHoverState(null);
            viewHelperContainer.style.cursor = 'default';
            [...Object.values(controlButtons), ...Object.values(arrowButtons)].forEach((button) => {
                const material = button.mesh.material as MeshBasicMaterial;
                if (material.map !== button.normal) {
                    material.map = button.normal;
                    material.needsUpdate = true;
                }
            });
        };

        this.render = function (renderer: any) {
            updateCubeOrientation();

            const useComparisonSizing = engine.comparisonModeActive;
            const dpr = useComparisonSizing ? 1 : (window.devicePixelRatio || 1);
            let ratio = 1;
            if (!useComparisonSizing && engine.session.currentGraphicsSettings) {
                if (engine.session.currentGraphicsSettings.UPSCALING === 1) {
                    ratio = 0.75;
                } else if (engine.session.currentGraphicsSettings.UPSCALING === 2) {
                    ratio = 0.5;
                } else if (engine.session.currentGraphicsSettings.UPSCALING === 3) {
                    ratio = 0.25;
                }
            }

            const canvasRect = domElement.getBoundingClientRect();
            const helperRect = viewHelperContainer.getBoundingClientRect();
            const insetWidth = helperRect.width - containerPadding * 2;
            const insetHeight = helperRect.height - containerPadding * 2;
            if (insetWidth <= 0 || insetHeight <= 0) return;
            const scale = dpr * ratio;
            const width = insetWidth * scale;
            const height = insetHeight * scale;

            const offsetX = (helperRect.left - canvasRect.left + containerPadding) * scale;
            const offsetY = (canvasRect.bottom - helperRect.bottom + containerPadding) * scale;

            renderer.clearDepth();

            renderer.getViewport(viewport);
            renderer.setViewport(offsetX, offsetY, width, height);

            // Render the scene (not just this) for proper lighting support
            renderer.render(helperScene, orthoCamera);

            renderer.setViewport(viewport.x, viewport.y, viewport.z, viewport.w);
        };

        const viewport = new Vector4();

        const refreshLabels = () => {
            Object.entries(labelText).forEach(([key, text]) => {
                const mesh = labelMeshes[key];
                if (!mesh) return;
                const material = mesh.material as MeshBasicMaterial;
                material.map?.dispose();
                material.map = createLabelTexture(text, currentThemeIsDark);
                material.needsUpdate = true;
            });
        };

        this.setLabels = (labelX: string, labelY: string, labelZ: string) => {
            labelText['posX'] = labelX;
            labelText['negX'] = `-${labelX}`;
            labelText['posY'] = labelY;
            labelText['negY'] = `-${labelY}`;
            labelText['posZ'] = labelZ;
            labelText['negZ'] = `-${labelZ}`;
            refreshLabels();
        };

        this.setLabelStyle = (font: string, color: string) => {
            options.font = font;
            options.color = color;
            refreshLabels();
        };

        this.setTheme = (isDark: boolean) => {
            if (currentThemeIsDark === isDark) return;
            currentThemeIsDark = isDark;

            // Update cube color and material for theme
            const newCubeColor = isDark ? cubeDarkColor : cubeLightColor;
            faceBaseColor.copy(newCubeColor);
            cubeMaterial.color.copy(newCubeColor);
            cubeMaterial.roughness = isDark ? 0.35 : 0.05;
            cubeMaterial.metalness = isDark ? 0.15 : 0.0;
            cubeMaterial.needsUpdate = true;

            // Update labels for theme
            refreshLabels();

            // Update control button textures
            Object.entries(controlButtons).forEach(([key, button]) => {
                const iconKey = key as 'center' | 'fit' | 'iso';
                const oldNormal = button.normal;
                const oldHover = button.hover;

                button.normal = createControlTexture(iconKey, false, isDark);
                button.hover = createControlTexture(iconKey, true, isDark);

                // Update the current map on the material
                const material = button.mesh.material as MeshBasicMaterial;
                material.map = button.normal;
                material.needsUpdate = true;

                oldNormal.dispose();
                oldHover.dispose();
            });

            // Update arrow button textures
            Object.entries(arrowButtons).forEach(([key, button]) => {
                const arrowKey = key as ArrowType;
                const oldNormal = button.normal;
                const oldHover = button.hover;

                button.normal = createArrowTexture(arrowKey, false, isDark);
                button.hover = createArrowTexture(arrowKey, true, isDark);

                const material = button.mesh.material as MeshBasicMaterial;
                material.map = button.normal;
                material.needsUpdate = true;

                oldNormal.dispose();
                oldHover.dispose();
            });

            // Update shadow for theme
            updateShadowTheme(isDark);
        };

        this.update = (delta: number) => {
            // Always update to allow smooth scale animation back to normal
            applyHoverState(delta);

            // Animate click scale feedback
            if (clickScale !== clickScaleTarget) {
                const diff = clickScaleTarget - clickScale;
                clickScale += diff * Math.min(1, delta * clickScaleSpeed);
                // Snap to target if close enough
                if (Math.abs(diff) < 0.001) {
                    clickScale = clickScaleTarget;
                }
                // Apply combined scale (base scale * click feedback)
                const baseScale = 1.25;
                cubeGroup.scale.setScalar(baseScale * clickScale);
            }
        };

        this.dispose = () => {
            cubeGeometry.dispose();
            lineGeometry.dispose();
            labelPlane.dispose();
            controlPlane.dispose();

            edges.material.dispose();

            // Dispose shadow
            shadowGeometry.dispose();
            shadowMaterial.dispose();
            shadowTexture.dispose();

            pickTargets.forEach((mesh) => mesh.geometry.dispose());
            controlTargets.forEach((mesh) => {
                const material = mesh.material as MeshBasicMaterial;
                material.map?.dispose();
                material.dispose();
            });
            cubeMaterial.dispose();

            // Dispose face, edge and corner highlights
            faceHighlights.forEach(({ mesh, outline }) => {
                mesh.geometry.dispose();
                (mesh.material as MeshBasicMaterial).dispose();
                outline.geometry.dispose();
                (outline.material as LineMaterial).dispose();
            });
            edgeHighlights.forEach(({ meshes, outlines }) => {
                meshes.forEach((mesh) => {
                    mesh.geometry.dispose();
                    (mesh.material as MeshBasicMaterial).dispose();
                });
                outlines.forEach((outline) => {
                    outline.geometry.dispose();
                    (outline.material as LineMaterial).dispose();
                });
            });
            cornerHighlights.forEach(({ meshes, outlines }) => {
                meshes.forEach((mesh) => {
                    mesh.geometry.dispose();
                    (mesh.material as MeshBasicMaterial).dispose();
                });
                outlines.forEach((outline) => {
                    outline.geometry.dispose();
                    (outline.material as LineMaterial).dispose();
                });
            });

            Object.values(labelMeshes).forEach((mesh) => {
                const material = mesh.material as MeshBasicMaterial;
                material.map?.dispose();
                material.dispose();
            });

            Object.values(controlButtons).forEach((button) => {
                button.normal.dispose();
                button.hover.dispose();
            });

            Object.values(arrowButtons).forEach((button) => {
                button.normal.dispose();
                button.hover.dispose();
            });
            arrowPlane.dispose();
            rollArrowPlane.dispose();

            // Dispose axis indicators
            [xAxisLine, yAxisLine, zAxisLine].forEach((line) => {
                line.geometry.dispose();
                (line.material as LineMaterial).dispose();
            });

            // Dispose axis labels
            axisLabelGeometry.dispose();
            axisLabels.forEach(({ mesh }) => {
                const material = mesh.material as MeshBasicMaterial;
                material.map?.dispose();
                material.dispose();
            });
        };

        const getUpForAxis = (axis: 'posX' | 'negX' | 'posY' | 'negY' | 'posZ' | 'negZ') => {
            return axis === 'posZ' || axis === 'negZ' ? altUp : worldUp;
        };

        this.rotateCameraToSide = (axis: 'posX' | 'negX' | 'posY' | 'negY' | 'posZ' | 'negZ') => {
            engine.clearOrbitPivot();
            const distance = camera.position.distanceTo(controls.target);
            const targetPosition = new Vector3();

            switch (axis) {
                case 'negX':
                    targetPosition.set(controls.target.x - distance, controls.target.y, controls.target.z);
                    break;
                case 'posX':
                    targetPosition.set(controls.target.x + distance, controls.target.y, controls.target.z);
                    break;
                case 'posY':
                    targetPosition.set(controls.target.x, controls.target.y + distance, controls.target.z);
                    break;
                case 'negY':
                    targetPosition.set(controls.target.x, controls.target.y - distance, controls.target.z);
                    break;
                case 'posZ':
                    targetPosition.set(controls.target.x, controls.target.y, controls.target.z + distance);
                    break;
                case 'negZ':
                    targetPosition.set(controls.target.x, controls.target.y, controls.target.z - distance);
                    break;
            }

            const targetUp = getUpForAxis(axis);

            const positionTween = new TWEEN.Tween(camera.position)
                .to({ x: targetPosition.x, y: targetPosition.y, z: targetPosition.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => {
                    camera.up.copy(targetUp);
                    camera.lookAt(controls.target);
                    controls.update();
                });

            const upTween = new TWEEN.Tween(camera.up)
                .to({ x: targetUp.x, y: targetUp.y, z: targetUp.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => camera.lookAt(controls.target));

            positionTween.start();
            upTween.start();
        };

        this.rotateCameraToNormal = (normal: Vector3, targetPoint?: Vector3) => {
            rotateCameraToDirection(normal, targetPoint);
        };

        this.centerCameraOnPoint = (point: Vector3) => {
            engine.clearOrbitPivot();
            const distance = camera.position.distanceTo(controls.target);
            const direction = new Vector3();
            camera.getWorldDirection(direction);

            const targetPosition = new Vector3().copy(point).sub(direction.multiplyScalar(distance));

            // For centering, direction doesn't change so up vector stays the same
            const positionTween = new TWEEN.Tween(camera.position)
                .to({ x: targetPosition.x, y: targetPosition.y, z: targetPosition.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => {
                    camera.lookAt(point);
                    controls.update();
                });

            const targetTween = new TWEEN.Tween(controls.target)
                .to({ x: point.x, y: point.y, z: point.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => controls.update());

            positionTween.start();
            targetTween.start();
        };

        function tweenToPromise(tween: any) {
            return new Promise((resolve) => {
                tween.onComplete(() => resolve(true));
            });
        }

        this.centerCameraOnObjectOrthographic = (object: Object3D<Object3DEventMap>) => {
            engine.clearOrbitPivot();
            const { center, size } = engine.getObjectBbox(object);

            const direction = new Vector3();
            camera.getWorldDirection(direction);
            const distance = camera.position.distanceTo(controls.target);

            const targetPosition = new Vector3();
            targetPosition.copy(center).sub(direction.multiplyScalar(distance));

            const positionTween = new TWEEN.Tween(camera.position)
                .to({ x: targetPosition.x, y: targetPosition.y, z: targetPosition.z }, 200)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => {
                    camera.lookAt(center);
                    controls.update();
                })
                .start();

            const controlsTween = new TWEEN.Tween(controls.target)
                .to({ x: center.x, y: center.y, z: center.z }, 200)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => controls.update())
                .start();

            return Promise.all([tweenToPromise(positionTween), tweenToPromise(controlsTween)]);
        };

        this.fitCameraToGroupAndSetIsometricOrthographic = async (targetGroup: Object3D<Object3DEventMap>, zoom: number = 0.5) => {
            engine.clearOrbitPivot();
            const { center, size } = engine.getObjectBbox(targetGroup);

            const isometricAngle = Math.PI / 4;
            const isometricElevation = Math.atan(Math.sqrt(2) / 1);

            const direction = new Vector3(
                Math.cos(isometricAngle),
                Math.sin(isometricElevation),
                Math.sin(isometricAngle)
            ).normalize();

            const distanceFactor = 2;
            const cameraDistance = distanceFactor * Math.max(size.x, size.y, size.z);

            const targetPosition = new Vector3().copy(center).addScaledVector(direction, cameraDistance);

            const targetUp = worldUp.clone();

            new TWEEN.Tween(camera)
                .to({ zoom: zoom }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => camera.updateProjectionMatrix())
                .start();

            new TWEEN.Tween(camera.position)
                .to({ x: targetPosition.x, y: targetPosition.y, z: targetPosition.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => camera.lookAt(center))
                .start();

            new TWEEN.Tween(camera.up)
                .to({ x: targetUp.x, y: targetUp.y, z: targetUp.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => camera.lookAt(center))
                .start();

            new TWEEN.Tween(controls.target)
                .to({ x: center.x, y: center.y, z: center.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => controls.update())
                .start();
        };

        this.fitCameraToObject = (targetGroup: Object3D<Object3DEventMap> | Box3, zoom: number = 0.5) => {
            engine.clearOrbitPivot();
            let boundingBox: Box3;
            if (targetGroup instanceof Box3) {
                boundingBox = new Box3().copy(targetGroup);
            } else if (targetGroup === engine.group) {
                boundingBox = engine.objectInfo.box.clone();
            } else {
                boundingBox = new Box3().setFromObject(targetGroup);
            }

            if (boundingBox.isEmpty()) return;

            const center = new Vector3();
            boundingBox.getCenter(center);
            const size = new Vector3();
            boundingBox.getSize(size);

            const distanceFactor = 2;
            const maxDim = Math.max(size.x, size.y, size.z);
            const cameraDirection = new Vector3();
            camera.getWorldDirection(cameraDirection);
            const targetPosition = new Vector3().copy(center).sub(cameraDirection.multiplyScalar(maxDim * distanceFactor));

            camera.updateMatrixWorld();
            const inverseMatrix = camera.matrixWorldInverse;
            const min = boundingBox.min;
            const max = boundingBox.max;

            const corners = [
                new Vector3(min.x, min.y, min.z),
                new Vector3(min.x, min.y, max.z),
                new Vector3(min.x, max.y, min.z),
                new Vector3(min.x, max.y, max.z),
                new Vector3(max.x, min.y, min.z),
                new Vector3(max.x, min.y, max.z),
                new Vector3(max.x, max.y, min.z),
                new Vector3(max.x, max.y, max.z)
            ];

            let minX = Infinity;
            let maxX = -Infinity;
            let minY = Infinity;
            let maxY = -Infinity;

            for (const corner of corners) {
                corner.applyMatrix4(inverseMatrix);
                minX = Math.min(minX, corner.x);
                maxX = Math.max(maxX, corner.x);
                minY = Math.min(minY, corner.y);
                maxY = Math.max(maxY, corner.y);
            }

            const width = Math.max(maxX - minX, 1e-6);
            const height = Math.max(maxY - minY, 1e-6);
            const frustumWidth = camera.right - camera.left;
            const frustumHeight = camera.top - camera.bottom;
            const padding = 1.05;
            let fitZoom = Math.min(frustumWidth / (width * padding), frustumHeight / (height * padding));
            if (!Number.isFinite(fitZoom) || fitZoom <= 0) {
                fitZoom = camera.zoom;
            }
            const zoomScale = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
            const targetZoom = Math.min(fitZoom * zoomScale, fitZoom);

            const zoomTween = new TWEEN.Tween(camera)
                .to({ zoom: targetZoom }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => camera.updateProjectionMatrix());

            const positionTween = new TWEEN.Tween(camera.position)
                .to({ x: targetPosition.x, y: targetPosition.y, z: targetPosition.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => camera.lookAt(center));

            const targetTween = new TWEEN.Tween(controls.target)
                .to({ x: center.x, y: center.y, z: center.z }, 220)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => controls.update());

            zoomTween.start();
            positionTween.start();
            targetTween.start();
        };
    }
}

export { ViewCubeHelper };
